import React, { useState } from 'react';

import {
  JabraRadioButton,
  JabraRadioButtonStyles,
  JabraRadioGroup,
} from 'common/components/JabraRadioGroup';
import { NavigationButtons } from 'composables/HearingTest/components/NavigationButtons';
import { StepContainer } from 'composables/HearingTest/components/StepContainer';

import JabraRangeSlider from '../../../common/components/JabraRangeSlider';
import { useHearingTest, useHearingTestTracking } from '../HearingTestContext';
import { translations } from '../translations/en';
import { STEP_NAMES, TOTAL_STEPS } from '../types/HearingTestTypes';
import { HearingTestEventAction } from '../types/TrackingTypes';

const AGREEMENT_LABELS: { [key: number]: string } = {
  1: translations.stronglydisagree,
  2: translations.disagree,
  3: translations.agree,
  4: translations.stronglyagree,
};

const WEARING_OPTIONS = [
  { value: 'as-needed', label: translations.situationineed },
  { value: 'convenient', label: translations.whenconventient },
  { value: 'all-day', label: translations.allday },
  { value: 'idontknow', label: translations.idontknow },
];

export const LifestyleQuestions2Step: React.FC = () => {
  const { state, updateProfile, goToStep } = useHearingTest();
  const { trackHearingTestEvent } = useHearingTestTracking();
  const [localProfile, setLocalProfile] = useState({
    designImportance: state.profile.designImportance,
    comfortImportance: state.profile.comfortImportance,
    wearingSituation: state.profile.wearingSituation,
  });

  const handleNext = () => {
    trackHearingTestEvent(HearingTestEventAction.SelectDesign, localProfile.designImportance || '');
    trackHearingTestEvent(
      HearingTestEventAction.SelectComfort,
      localProfile.comfortImportance || ''
    );
    trackHearingTestEvent(
      HearingTestEventAction.SelectWearing,
      localProfile.wearingSituation || ''
    );

    updateProfile(localProfile);
    goToStep(STEP_NAMES.SOUND_CHECK);
  };

  const handleInputChange = (field: keyof typeof localProfile, value: number | string | null) => {
    setLocalProfile((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isValid =
    localProfile.designImportance &&
    localProfile.comfortImportance &&
    localProfile.wearingSituation;

  return (
    <StepContainer currentStep={state.currentStep} totalSteps={TOTAL_STEPS}>
      <div className="mb-8 text-center">
        <h1 className="mb-4 text-3xl font-bold text-gray-900">{translations.ls2headline}</h1>
        <p className="text-lg text-gray-700">{translations.ls2description}</p>
      </div>

      <div className="space-y-12">
        <div>
          <h2 id="design-importance-label" className="mb-6 text-xl font-semibold text-gray-900">
            {translations.appreciateproductdesign} <span aria-label={translations.required}>*</span>
          </h2>
          <div>
            <JabraRangeSlider
              ariaLabelledBy="design-importance-label"
              values={[localProfile.designImportance || 0]}
              onValueChange={([value]) => handleInputChange('designImportance', value)}
              min={1}
              max={4}
              step={1}
              showStepLabels
              labelFormatter={(value) => AGREEMENT_LABELS[value]}
            />
            {!localProfile.designImportance && (
              <p className="mt-2 text-sm text-error-600">{translations.designimportancehelp}</p>
            )}
          </div>
        </div>

        <div>
          <h2 id="comfort-importance-label" className="mb-6 text-xl font-semibold text-gray-900">
            {translations.comfortisimportant} <span aria-label={translations.required}>*</span>
          </h2>
          <div>
            <JabraRangeSlider
              ariaLabelledBy="comfort-importance-label"
              values={[localProfile.comfortImportance || 0]}
              onValueChange={([value]) => handleInputChange('comfortImportance', value)}
              min={1}
              max={4}
              step={1}
              showStepLabels
              labelFormatter={(value: number) => AGREEMENT_LABELS[value]}
            />
            {!localProfile.comfortImportance && (
              <p className="mt-2 text-sm text-error-600">{translations.comfortimportancehelp}</p>
            )}
          </div>
        </div>

        <div>
          <h2 className="mb-6 text-xl font-semibold text-gray-900">
            {translations.wearaproduct} <span aria-label={translations.required}>*</span>
          </h2>
          <JabraRadioGroup
            title="Wearing Situation"
            selected={localProfile.wearingSituation || ''}
            onValueChange={(value) => handleInputChange('wearingSituation', value)}
            className="flex flex-col gap-3"
          >
            {WEARING_OPTIONS.map((option) => (
              <JabraRadioButton
                key={option.value}
                value={option.value}
                title={option.label}
                selectedStyle={JabraRadioButtonStyles.primary}
              />
            ))}
          </JabraRadioGroup>
          {!localProfile.wearingSituation && (
            <p className="mt-2 text-sm text-error-600">{translations.wearingsituationhelp}</p>
          )}
        </div>
      </div>

      <NavigationButtons onNext={handleNext} disableNext={!isValid} />
    </StepContainer>
  );
};

export default LifestyleQuestions2Step;
